import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../utils/axiosConfig";
import Swal from "sweetalert2";
import EditSubscriptions from "./editSubscriptions";
import EditNotifications from "./editNotifications";
import EditPreferances from "./editPreferences";
import EditAuthentication from "./editauthentication";
import EditProductivity from "./editProductivity";
import EditPermission from "./editPermission";
import EditLabDetails from "./editLabDetails";
import { useAuth } from "../EditUser/AuthProvider";

const EditUser = () => {
  const { userId } = useParams();
  const { user } = useAuth();

  // Get userRole from localStorage to determine admin access
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "Admin";

  // Main state holding all user data
  const [state, setState] = useState({
    salutation: "",
    first_name: "",
    last_name: "",
    middleInitial: "",
    userId: "",
    email: "",
    username: "",
    labEmail: "",
    labName: "",
    address: "",
    address2: "",
    city: "",
    state: "",
    country: "",
    zipCode: "",
    telephone: "",
    timeZone: "UTC",
    subdomain: "",
    services: [],
    newsletter: false,
    trainingInterest: false,
    emailNotifications: {},
    webNotifications: {},
    preferences: {
      teethNotation: "Universal",
      language: "English (US)",
      disableRxTyping: false,
      technicianStamp: "",
    },
    productivity: {},
    permissions: {},
    authentication: {
      id: "",
      userName: "",
      password: "",
      confirmUserName: "",
      currentPasswordMask: "********",
      confirmPassword: "",
      noEmail: false,
      firstName: "",
      lastName: "",
    },
    labDetails: {
      labName: "",
      labEmail: "",
      address: "",
      address2: "",
      city: "",
      state: "",
      country: "",
      zipCode: "",
      telephone: "",
      Subdomain: ""
    },
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Fetch user data when component mounts or userId changes
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (!userId) {
          throw new Error("User ID not found in URL");
        }

        console.log("Fetching user data for ID:", userId);
        const response = await api.get(`/auth/users/${userId}`);

        if (response.data.success) {
          const userData = response.data.user;
          const fullName = `${userData.first_name || ""} ${userData.last_name || ""}`.trim();
          sessionStorage.setItem("username", fullName);
          localStorage.setItem("userEmail", userData.email || "");

          const noEmail = !!userData.username && !userData.email;
          const userName = noEmail ? userData.username : userData.email;

          setState((prev) => ({
            ...prev,
            ...userData,
            emailNotifications: userData.emailNotifications || {},
            webNotifications: userData.webNotifications || {},
            authentication: {
              ...prev.authentication,
              id: userData.userId?.toString() || "",
              userName,
              confirmUserName: userName,
              noEmail,
              firstName: userData.first_name || "",
              lastName: userData.last_name || "",
            },
            labDetails: {
              labName: userData.labName || "",
              labEmail: userData.labEmail || "",
              address: userData.address || "",
              address2: userData.address2 || "",
              city: userData.city || "",
              state: userData.state || "",
              country: userData.country || "",
              zipCode: userData.zipCode || "",
              telephone: userData.telephone || "",
              subdomain: userData.subdomain || "",
            },
          }));
        } else {
          throw new Error(response.data.message || "Failed to fetch user data");
        }
      } catch (err) {
        console.error("Fetch error details:", err);
        setError(err.response?.data?.message || err.message || "Failed to fetch user data");
        
        if (err.response?.status === 401) {
          Swal.fire({
            icon: "error",
            title: "Session Expired",
            text: "Please login again to continue",
            confirmButtonColor: "#d33",
          }).then(() => {
            navigate("/login");
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error Loading User",
            text: err.response?.data?.message || err.message || "Failed to fetch user data",
            confirmButtonColor: "#d33",
          });
        }
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserData();
    }
  }, [userId, navigate]);

  // Handle changes in basic user fields
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setState((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle authentication section changes
  const handleAuthenticationChange = (updatedAuthData) => {
    setState((prev) => ({
      ...prev,
      authentication: { ...prev.authentication, ...updatedAuthData },
    }));
  };

  // Handle subscriptions checkbox changes
  const handleSubscriptionsChange = (fieldType, fieldName, checked) => {
    setState((prev) => ({
      ...prev,
      [fieldType]: {
        ...prev[fieldType],
        [fieldName]: checked,
      },
    }));
  };

  // Handle notifications changes from child component
  const handleNotificationsChange = (updatedNotificationsData) => {
    setState((prev) => ({
      ...prev,
      emailNotifications: updatedNotificationsData.emailNotifications || prev.emailNotifications,
      webNotifications: updatedNotificationsData.webNotifications || prev.webNotifications,
    }));
  };

  // Handle lab details changes
  const handleLabDetailsChange = (updatedLabDetails) => {
    setState((prev) => ({
      ...prev,
      labDetails: updatedLabDetails,
      labName: updatedLabDetails.labName,
      labEmail: updatedLabDetails.labEmail,
      address: updatedLabDetails.address,
      address2: updatedLabDetails.address2,
      city: updatedLabDetails.city,
      state: updatedLabDetails.state,
      country: updatedLabDetails.country,
      zipCode: updatedLabDetails.zipCode,
      telephone: updatedLabDetails.telephone,
      subdomain: updatedLabDetails.subdomain,
    }));
  };

  // Save basic user information (top form)
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!userId) {
        throw new Error("User ID not found in URL");
      }

      Swal.fire({
        title: "Updating profile...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await api.put(`/auth/${userId}`, state);

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Profile updated successfully!",
          confirmButtonColor: "#004aad",
          timer: 2000,
        });

        setState((prev) => ({
          ...prev,
          ...response.data.user,
        }));
      } else {
        throw new Error(response.data.message || "Failed to update profile");
      }
    } catch (err) {
      console.error("Update error:", err);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: err.response?.data?.message || err.message || "Failed to update profile",
        confirmButtonColor: "#d33",
      });
    }
  };

  // ✅ FIXED: Handle authentication save - Now fields are optional
  const handleAuthenticationSubmit = async (authData) => {
    const { userName, password, confirmUserName, confirmPassword, noEmail } = authData;
    
    // Prepare update data - only include fields that are filled
    const updateData = {};
    
    // Handle username/email update
    if (userName && userName.trim() !== "") {
      // Check if username/email has actually changed
      const currentValue = noEmail ? state.username : state.email;
      if (userName !== currentValue) {
        if (noEmail) {
          updateData.username = userName;
        } else {
          updateData.email = userName;
        }
      }
    }
    
    // Handle password update - only if password is provided
    if (password && password.trim() !== "") {
      // Validate password confirmation
      if (password !== confirmPassword) {
        Swal.fire({
          icon: "error",
          title: "Mismatch",
          text: "Passwords do not match.",
          confirmButtonColor: "#d33",
        });
        return false;
      }
      
      // Validate password length
      if (password.length < 4) {
        Swal.fire({
          icon: "warning",
          title: "Password Too Short",
          text: "Password must be at least 4 characters long.",
          confirmButtonColor: "#004aad",
        });
        return false;
      }
      
      updateData.password = password;
    }
    
    // Check if there's anything to update
    if (Object.keys(updateData).length === 0) {
      Swal.fire({
        icon: "info",
        title: "No Changes",
        text: "No changes were made to authentication details.",
        confirmButtonColor: "#004aad",
      });
      return false;
    }

    try {
      Swal.fire({
        title: "Updating authentication details...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await api.put(`/auth/${userId}`, updateData);

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Authentication details updated successfully!",
          confirmButtonColor: "#004aad",
          timer: 2000,
        });

        // Update local state
        if (updateData.email) {
          localStorage.setItem("userEmail", updateData.email);
          setState((prev) => ({
            ...prev,
            email: updateData.email,
          }));
        }
        if (updateData.username) {
          setState((prev) => ({
            ...prev,
            username: updateData.username,
          }));
        }
        
        // Clear password fields in the form
        handleAuthenticationChange({
          password: "",
          confirmPassword: "",
        });
        
        return true;
      } else {
        throw new Error(response.data.message || "Failed to update authentication details");
      }
    } catch (error) {
      console.error("Auth update error:", error);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: error.response?.data?.message || error.message || "Failed to update authentication details",
        confirmButtonColor: "#d33",
      });
      return false;
    }
  };

  // Handle subscriptions save
  const handleSubscriptionsSubmit = async (subscriptionsData) => {
    try {
      Swal.fire({
        title: "Updating subscriptions...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await api.put(`/auth/${userId}`, subscriptionsData);

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Subscriptions updated successfully!",
          confirmButtonColor: "#004aad",
          timer: 2000,
        });
        return true;
      } else {
        throw new Error(response.data.message || "Failed to update subscriptions");
      }
    } catch (error) {
      console.error("Subscriptions update error:", error);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: error.response?.data?.message || error.message || "Failed to update subscriptions",
        confirmButtonColor: "#d33",
      });
      return false;
    }
  };

  // Handle notifications save
  const handleNotificationsSubmit = async (notificationsData) => {
    try {
      Swal.fire({
        title: "Updating notifications...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await api.put(`/auth/${userId}`, notificationsData);

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Notifications updated successfully!",
          confirmButtonColor: "#004aad",
          timer: 2000,
        });
        return true;
      } else {
        throw new Error(response.data.message || "Failed to update notifications");
      }
    } catch (error) {
      console.error("Notifications update error:", error);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: error.response?.data?.message || error.message || "Failed to update notifications",
        confirmButtonColor: "#d33",
      });
      return false;
    }
  };

  // Handle preferences save
  const handlePreferencesSubmit = async ({ preferences }) => {
    try {
      Swal.fire({
        title: "Updating preferences...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await api.put(`/auth/${userId}`, { preferences });

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Preferences updated successfully!",
          confirmButtonColor: "#004aad",
          timer: 2000,
        });

        setState((prev) => ({
          ...prev,
          preferences: response.data.user.preferences || prev.preferences,
        }));
        return true;
      } else {
        throw new Error(response.data.message || "Failed to update preferences");
      }
    } catch (error) {
      console.error("Preferences update error:", error);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: error.response?.data?.message || error.message || "Failed to update preferences",
        confirmButtonColor: "#d33",
      });
      return false;
    }
  };

  // Handle lab details save
  const handleLabDetailsSubmit = async ({ labDetails }) => {
    try {
      Swal.fire({
        title: "Updating lab details...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const updateData = {
        labName: labDetails.labName,
        labEmail: labDetails.labEmail,
        address: labDetails.address,
        address2: labDetails.address2,
        city: labDetails.city,
        state: labDetails.state,
        country: labDetails.country,
        zipCode: labDetails.zipCode,
        telephone: labDetails.telephone,
        subdomain: labDetails.subdomain,
      };

      const response = await api.put(`/auth/${userId}`, updateData);

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Lab details updated successfully!",
          confirmButtonColor: "#004aad",
          timer: 2000,
        });

        setState((prev) => ({
          ...prev,
          ...updateData,
          labDetails: {
            ...prev.labDetails,
            ...labDetails,
          },
        }));
        return true;
      } else {
        throw new Error(response.data.message || "Failed to update lab details");
      }
    } catch (error) {
      console.error("Lab details update error:", error);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: error.response?.data?.message || error.message || "Failed to update lab details",
        confirmButtonColor: "#d33",
      });
      return false;
    }
  };

  // Handle productivity save (admin only)
  const handleProductivitySubmit = async (productivityData) => {
    try {
      Swal.fire({
        title: "Updating productivity settings...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await api.put(`/auth/${userId}`, productivityData);

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Productivity updated successfully!",
          confirmButtonColor: "#004aad",
          timer: 2000,
        });

        setState((prev) => ({
          ...prev,
          productivity: response.data.user.productivity || prev.productivity,
        }));
        return true;
      } else {
        throw new Error(response.data.message || "Failed to update productivity");
      }
    } catch (error) {
      console.error("Productivity update error:", error);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: error.response?.data?.message || error.message || "Failed to update productivity",
        confirmButtonColor: "#d33",
      });
      return false;
    }
  };

  // Handle permissions save (admin only)
  const handlePermissionSubmit = async (permissionData) => {
    try {
      Swal.fire({
        title: "Updating permissions...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await api.put(`/auth/${userId}`, permissionData);

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Permissions updated successfully!",
          confirmButtonColor: "#004aad",
          timer: 2000,
        });

        setState((prev) => ({
          ...prev,
          permissions: response.data.user.permissions || prev.permissions,
        }));
        return true;
      } else {
        throw new Error(response.data.message || "Failed to update permissions");
      }
    } catch (error) {
      console.error("Permissions update error:", error);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: error.response?.data?.message || error.message || "Failed to update permissions",
        confirmButtonColor: "#d33",
      });
      return false;
    }
  };

  // Show loading while fetching user data
  if (loading && !error) {
    return (
      <div className="text-center mt-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="mt-2">Loading user data...</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ width: "95%", margin: "0 auto", padding: "0px" }}>
        <div className="row">
          <div className="col-md-10">
            {error && <div className="text-center mt-5 text-danger">Error: {error}</div>}
            <h1 style={{ marginBottom: "10px" }}>Edit User</h1>

            {/* Basic Information Form */}
            <form onSubmit={handleSubmit}>
              <div
                style={{
                  border: "1px solid #e9ecef",
                  borderTop: "3px solid #004aad",
                  backgroundColor: "#e9ecef",
                  padding: "10px",
                }}
              >
                <div style={{ marginBottom: "10px", display: "flex", alignItems: "center" }}>
                  <label style={{ fontWeight: "bold", marginLeft: "10px", width: "30%", color: "#333" }}>
                    Salutation
                  </label>
                  <input
                    type="text"
                    name="salutation"
                    value={state.salutation}
                    onChange={handleInputChange}
                    style={{
                      padding: "8px",
                      borderRadius: "4px",
                      border: "1px solid #ced4da",
                      width: "60%",
                      backgroundColor: "#fff",
                    }}
                  />
                </div>

                <div style={{ marginBottom: "10px", display: "flex", alignItems: "center" }}>
                  <label style={{ fontWeight: "bold", marginLeft: "10px", width: "30%", color: "#333" }}>
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={state.first_name}
                    onChange={handleInputChange}
                    style={{
                      padding: "8px",
                      borderRadius: "4px",
                      border: "1px solid #ced4da",
                      width: "60%",
                      backgroundColor: "#fff",
                    }}
                  />
                </div>

                <div style={{ marginBottom: "10px", display: "flex", alignItems: "center" }}>
                  <label style={{ fontWeight: "bold", marginLeft: "10px", width: "30%", color: "#333" }}>
                    Middle Initial
                  </label>
                  <input
                    type="text"
                    name="middleInitial"
                    value={state.middleInitial || ""}
                    onChange={handleInputChange}
                    style={{
                      padding: "8px",
                      borderRadius: "4px",
                      border: "1px solid #ced4da",
                      width: "60%",
                      backgroundColor: "#fff",
                    }}
                  />
                </div>

                <div style={{ marginBottom: "10px", display: "flex", alignItems: "center" }}>
                  <label style={{ fontWeight: "bold", marginLeft: "10px", width: "30%", color: "#333" }}>
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={state.last_name}
                    onChange={handleInputChange}
                    style={{
                      padding: "8px",
                      borderRadius: "4px",
                      border: "1px solid #ced4da",
                      width: "60%",
                      backgroundColor: "#fff",
                    }}
                  />
                </div>
                
                {/* Username Field */}
                <div style={{ marginBottom: "10px", display: "flex", alignItems: "center" }}>
                  <label style={{ fontWeight: "bold", marginLeft: "10px", width: "30%", color: "#333" }}>
                    Username *
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={state.username || ""}
                    onChange={handleInputChange}
                    style={{
                      padding: "8px",
                      borderRadius: "4px",
                      border: "1px solid #ced4da",
                      width: "60%",
                      backgroundColor: "#fff",
                    }}
                  />
                </div>

                <div style={{ marginBottom: "10px", display: "flex", alignItems: "center" }}>
                  <label style={{ fontWeight: "bold", marginLeft: "10px", width: "30%", color: "#333" }}>
                    External ID
                  </label>
                  <input
                    type="text"
                    name="userId"
                    value={state.userId || ""}
                    onChange={handleInputChange}
                    style={{
                      padding: "8px",
                      borderRadius: "4px",
                      border: "1px solid #ced4da",
                      width: "60%",
                      backgroundColor: "#fff",
                    }}
                  />
                </div>
              </div>

              <div className="mt-3" style={{ textAlign: "right" }}>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ marginRight: "20px", backgroundColor: "#004aad", borderColor: "#004aad" }}
                >
                  Save Basic Changes
                </button>
              </div>
            </form>

            {/* Lab Details Component */}
            <div className="mt-3">
              <EditLabDetails
                labDetails={state.labDetails}
                onChange={handleLabDetailsChange}
                onSubmit={handleLabDetailsSubmit}
                isAdmin={isAdmin}
              />
            </div>

            {/* Child Components for Advanced Settings */}
            <div className="mt-3">
              <EditAuthentication
                authentication={state.authentication}
                onChange={handleAuthenticationChange}
                onSubmit={handleAuthenticationSubmit}
              />
            </div>

            <div className="mt-3">
              <EditSubscriptions
                userData={{
                  email: state.email,
                  emailNotifications: state.emailNotifications,
                }}
                onUpdate={handleSubscriptionsSubmit}
                onFieldChange={handleSubscriptionsChange}
              />
            </div>

            <div className="mt-3">
              <EditNotifications
                notifications={{
                  emailNotifications: state.emailNotifications,
                  webNotifications: state.webNotifications,
                }}
                onChange={handleNotificationsChange}
                onSubmit={handleNotificationsSubmit}
              />
            </div>

            <div className="mt-3">
              <EditPreferances
                preferences={state.preferences}
                onChange={(updatedPreferences) => {
                  setState((prev) => ({
                    ...prev,
                    preferences: updatedPreferences,
                  }));
                }}
                onSubmit={handlePreferencesSubmit}
              />
            </div>

            {/* Admin-only sections */}
            {isAdmin && (
              <div className="mt-3">
                <EditProductivity
                  productivity={state.productivity}
                  onChange={(updatedProductivity) => {
                    setState((prev) => ({
                      ...prev,
                      productivity: updatedProductivity,
                    }));
                  }}
                  onSubmit={handleProductivitySubmit}
                />
              </div>
            )}

            {isAdmin && (
              <div className="mt-3">
                <EditPermission
                  permissions={state.permissions}
                  onChange={(updatedPermissions) => {
                    setState((prev) => ({
                      ...prev,
                      permissions: updatedPermissions,
                    }));
                  }}
                  onSubmit={handlePermissionSubmit}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditUser;